import type { ReactElement } from 'react';
import type { RarityLiterval, RaceLiterval, CareerLiterval } from '@/types/nftAttribute';

import React, { Suspense } from 'react';
import Head from 'next/head';
import tw, { styled } from 'twin.macro';
import {
  Box,
  Heading,
  Button,
  Tag,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  ButtonGroup,
  Spinner,
  IconButton,
} from '@chakra-ui/react';
import Link from 'next/link';
import { atom, useAtom, useAtomValue } from 'jotai';
import { atomWithQuery } from 'jotai/query';
import * as R from 'ramda';

import { RACE_TYPE_CHAIN_MAP, CAREER_TYPE_CHAIN_MAP } from '@/types/nftAttribute';
import AppLayout from '@/providers/AppLayout';
import ChakraThemeProvider from '@/providers/ChakraThemeProvider';
import ConditionallyRender from '@/components/ConditionallyRender';
import { subqlEndpointAtom } from '@/atoms/subqlEndpointAtom';
import { queryFedRanking } from '@/features/pwIncubation/queries';
import LevelIcon from '@/components/LevelIcon';
import RaceAvatar from '@/components/RaceAvatar/RaceRavatar';
import trimAddress from '@/features/identity/trimAddress';
import { currentAccountAtom } from '@/features/identity/atoms';
import ShareButton from '@/components/ShareButton';
import { SpeciesIcon } from '@/components/icons/SpeciesIcon';
import { CareerIcon } from '@/components/icons/CareerIcon';
import { trpcJotai } from '@/providers/trpc';

import IconWiki from '../../lib/svgs/wiki.svg';

//
//
//

interface BasicShellInfo {
  owner: string
  ownerDisplayName?: string
  collectionId: number
  nftId: number
  race: RaceLiterval
  rarity: RarityLiterval
  career: CareerLiterval
  rank: number
  feds: number
}

const selectedEraAtom = atom<number | null>(null)

const foodLeaderBoardAtom = atomWithQuery(get => {
  const subqlPrefix = get(subqlEndpointAtom)
  const selectedEra = get(selectedEraAtom)
  return queryFedRanking(subqlPrefix, selectedEra)
})

const shellFedRankForOwnerAtom = trpcJotai.campaigns.shellFedRankForOwner.atomWithQuery(get => {
  const account = get(currentAccountAtom)
  return account?.address || ''
})

const higestRankAtom = atom(async get => R.head<BasicShellInfo>(get(shellFedRankForOwnerAtom)))

//
//
//

interface TableCellProps {
  collectionId: number;
  nftId: number;
  rank: number;
  isCurrentUser?: boolean;
  displayName?: string | null;
  ownerAddress: string;
  rarity: string;
  career: string;
  race: string;
  feds: number;
}

const FoodRankingTableCell = ({
  collectionId,
  nftId,
  rank,
  isCurrentUser,
  displayName,
  ownerAddress,
  rarity,
  career,
  race,
  feds,
}: TableCellProps) => {
  const addr = trimAddress(ownerAddress)
  return (
    <Link href={`/collections/${collectionId}/${nftId}`} legacyBehavior>
      <Tr bg='rgba(22, 27, 31, 0.8)' rounded='sm' cursor='pointer'>
        <Td>
          <div tw='flex flex-row gap-8 items-center'>
            <div css={[rank > 3 && tw`invisible`]}>
              <LevelIcon level={rank} size={10} colorScheme={rarity.toLowerCase()} />
            </div>
            <span>{rank}</span>
            {isCurrentUser && <span>YOU</span>}
          </div>
        </Td>
        <Td>
          <div tw='flex flex-row gap-4 items-center'>
            <RaceAvatar race={race} rarity={rarity} size='48px' />
            <span>
              {RACE_TYPE_CHAIN_MAP[race]} #{nftId}
            </span>
          </div>
        </Td>
        <Td>
          <div tw='flex flex-col'>
            <span tw='text-base text-white'>{displayName || addr}</span>
            <span tw='text-sm text-gray-500 underline'>{addr}</span>
          </div>
        </Td>
        <Td>
          <span tw='ml-0.5 font-semibold text-lg'>{feds}</span>
        </Td>
        <Td>
          <div tw='flex flex-row gap-8'>
            <span tw='inline-flex flex-row gap-2.5 items-center text-white min-w-[8rem]'>
              <SpeciesIcon race={race} tw='w-6 h-6' />
              {RACE_TYPE_CHAIN_MAP[race]}
            </span>
            <span tw='inline-flex flex-row gap-2.5 items-center text-white'>
              <CareerIcon career={career} tw='w-6 h-6' />
              {CAREER_TYPE_CHAIN_MAP[career].replace('Web3.0', 'Web 3.0')}
            </span>
          </div>
        </Td>
        <Td>
          <Tag colorScheme={rarity.toLowerCase()} size='sm'>
            {rarity}
          </Tag>
        </Td>
      </Tr>
    </Link>
  );
}

const LeaderboardTable = () => {
  const data = useAtomValue(foodLeaderBoardAtom)
  const currentAccount = useAtomValue(currentAccountAtom)
  return (
    <TableContainer>
      <Table variant='unstyled' css={['border-collapse: separate !important; border-spacing: 0 10px;']}>
        <Thead>
          <Tr>
            <Th>Rank</Th>
            <Th>Origin Shell</Th>
            <Th>Owner</Th>
            <Th>Fed</Th>
            <Th>Shell Info</Th>
            <Th>Shell Rarity</Th>
          </Tr>
        </Thead>
        <Tbody>
          {data.slice(0, 10).map((i, index) => (
            <FoodRankingTableCell
              key={index}
              collectionId={i.collectionId}
              nftId={i.nftId}
              rarity={i.rarity}
              race={i.race}
              career={i.career}
              rank={i.rank}
              feds={i.feds}
              isCurrentUser={i.owner === currentAccount?.address}
              displayName={i.ownerDisplayName}
              ownerAddress={i.owner}
            />
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  )
}

const getShellRarityGradient = R.cond<any[], string>([
  [R.equals('Prime'), R.always('linear-gradient(90deg, rgba(54, 156, 250, 0) 56.34%, rgba(54, 97, 250, 0.3) 100%)')],
  [R.equals('Magic'), R.always('linear-gradient(90deg, rgba(217, 27, 248, 0) 56.34%, rgba(137, 27, 248, 0.3) 100%)')],
  [R.equals('Legendary'), R.always('linear-gradient(90deg, rgba(248, 136, 27, 0) 56.34%, rgba(248, 80, 27, 0.3) 100%)')],
  [R.T, R.always('rgba(22, 27, 31, 0.8)')],
])

const ShellSelectBackdrop = styled.div<{
  rarity: RarityLiterval
}>`
  ${tw`flex relative rounded-sm overflow-hidden`}
  height: 126px;
  background: ${({rarity}) => getShellRarityGradient(rarity)}, linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), rgba(22, 27, 31, 0.8);
  backdrop-filter: blur(10px);
`

const CurrentUserInfo = () => {
  const currentAccount = useAtomValue(currentAccountAtom)
  const highestRank = useAtomValue(higestRankAtom)
  if (!currentAccount || !highestRank) {
    return null
  }
  const addr = trimAddress(currentAccount.address)
  return (
    <section tw='flex flex-col lg:flex-row gap-5'>
      <div tw='rounded-sm bg-brand flex justify-center items-center h-32 lg:w-[126px] lg:h-[126px]'>
        <div tw='text-black text-center'>
          <h3 tw='text-4xl font-bold font-secondary'>{highestRank.rank}</h3>
          <p tw='uppercase text-base'>Your Rank</p>
        </div>
      </div>
      <div tw='flex flex-col gap-5 flex-1'>
        <ShellSelectBackdrop rarity={highestRank.rarity}>
          <div tw='flex flex-row gap-14 2xl:gap-20 items-center w-full relative z-10 px-8 py-5'>
            <section tw='flex flex-row items-center gap-5'>
              <Link href={`/profiles/${currentAccount.address}`} passHref>

                <RaceAvatar race={highestRank.race} rarity={highestRank.rarity} size='80px' />

              </Link>
              <div>
                <h4 tw='text-base 2xl:text-xl font-medium font-secondary'>{highestRank.ownerDisplayName || addr}</h4>
                <Link
                  href={`/profiles/${currentAccount.address}`}
                  passHref
                  tw='text-sm text-gray-500 underline'>
                  {addr}
                </Link>
              </div>
            </section>
            <section tw='flex flex-col gap-1'>
              <div tw='text-xs text-[rgba(255, 255, 255, 0.75)]'>Origin Shell</div>
              <h2 tw='text-base 2xl:text-xl font-bold font-secondary'>{RACE_TYPE_CHAIN_MAP[highestRank.race]} #{highestRank.nftId}</h2>
            </section>
            <section tw='flex flex-col gap-1'>
              <div tw='text-xs text-[rgba(255, 255, 255, 0.75)]'>Fed</div>
              <h2 tw='text-base 2xl:text-xl font-bold font-secondary'>{highestRank.feds}</h2>
            </section>
            <section tw='flex flex-col gap-1 text-sm 2xl:text-base'>
              <div tw='inline-flex gap-2 items-center'>
                <SpeciesIcon race={highestRank.race} tw='h-7 w-7' />
                {RACE_TYPE_CHAIN_MAP[highestRank.race]}
              </div>
              <div tw='inline-flex gap-2 items-center'>
                <CareerIcon career={highestRank.career} tw='h-7 w-7' />
                {CAREER_TYPE_CHAIN_MAP[highestRank.career]}
              </div>
            </section>
            <section>
              <Tag colorScheme={highestRank.rarity.toLowerCase()} size='sm'>{highestRank.rarity}</Tag>
            </section>
          </div>
        </ShellSelectBackdrop>
      </div>
    </section>
  );
}

const HeroHeading = styled.section`
  background: url('/static/leaderboards/heading-bg.jpg') no-repeat 0 0;
  background-size: cover;
  width: 100%;
  height: 240px;
  margin-top: 88px;
`;

const IncubationCampaignAlert = () => {
  return (
    <Box bg="brand.500" display="flex" flexDir="row" justifyContent="center" p="1.25rem" borderRadius="2px">
      <div tw="flex flex-row gap-4 text-black">
        <strong>Validity 00:00 Oct. 15, 2022 - 23:59 Oct. 23, 2022 SGT</strong>
        <p tw="font-light">After the event, we will airdrop new NFTs for the winners.</p>
      </div>
    </Box>
  )
}

export default function FoodLeaderBoardPage() {
  return (
    <ChakraThemeProvider>
      <Head>
        <title>Food Leaderboard | PhalaWorld</title>
      </Head>
      <ConditionallyRender client>
        <HeroHeading>
          <div tw='grid grid-cols-12 max-w-10xl px-5 xl:mx-0 3xl:mx-auto'>
            <header tw="col-start-2 col-span-10 flex flex-col justify-center h-[240px] relative z-10">
              <Heading as='h1' fontSize='52px' fontWeight='700' fontFamily='Orbitron' color='black'>
                FOOD
              </Heading>
              <Heading as='h2' fontSize='36px' fontWeight='700' fontFamily='Orbitron' color='black'>
                LEADERBOARD
              </Heading>
            </header>
            <img
              src='/static/leaderboards/silhouette-shells.png'
              alt=''
              tw='absolute top-0 right-0 z-0 pointer-events-none w-[949px] h-[240px]'
            />
          </div>
        </HeroHeading>

        <div tw='grid grid-cols-12 gap-5 max-w-10xl px-5 xl:mx-0 3xl:mx-auto pt-10 pb-10'>
          {/* <div tw="col-start-2 col-span-10">
            <IncubationCampaignAlert />
          </div> */}
          <div tw='col-start-2 col-span-10'>
            <Suspense>
              <CurrentUserInfo />
            </Suspense>
            <div tw='flex flex-row justify-end gap-2.5 py-5'>
              <ShareButton
                text="It's not an all-you-can-eat competition, you know? Really, 'cause I am winning. #PhalaWorld #Incubation #Leaderboard"
              />
              <IconButton aria-label='wiki' variant='secondary'>
                <IconWiki tw='h-6 w-6 text-white' />
              </IconButton>
            </div>
            <Suspense
              fallback={(
                <TableContainer>
                  <div tw='flex justify-center p-5'>
                    <Spinner />
                  </div>
                </TableContainer>
              )}
            >
              <LeaderboardTable />
            </Suspense>
          </div>
        </div>
      </ConditionallyRender>
    </ChakraThemeProvider>
  );
}

FoodLeaderBoardPage.getLayout = (page: ReactElement) => {
  return <AppLayout>{page}</AppLayout>;
};
